import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import usePersistedState, { StorageType } from '../../../hooks/use-persisted-state';
import { selectBets } from '../ducks';
import { BetslipPersistedBet } from '../sportsbook-betslip-types';
import { betToPersistedBet } from '../sportsbook-betslip-utils';
import { selectSportsbookUnifiedFeedEnabled } from '~/components/brand/ducks/selectors';
import normalizeUnifiedId from '../../../utils/normalize-unified-id';

const BETS_STORAGE_KEY = 'betslipBets';

const usePersistedBets = (enableWriting: boolean) => {
	const bets = useSelector(selectBets);
	const isUnifiedFeedEnabled = useSelector(selectSportsbookUnifiedFeedEnabled);

	const [persistedBets, setPersistedBets] = usePersistedState<BetslipPersistedBet[]>(
		BETS_STORAGE_KEY,
		StorageType.SESSION_STORAGE,
		[]
	);

	// Unify ids in case there are any persisted bets when moving over to the unified feed
	const unifiedPersistedBets = useMemo(() => {
		if (!isUnifiedFeedEnabled || !persistedBets) {
			return persistedBets;
		}

		return persistedBets.map(bet => ({
			...bet,
			items: bet.items.map(item => ({
				...item,
				eventId: normalizeUnifiedId(item.eventId),
				marketId: normalizeUnifiedId(item.marketId),
				selectionId: normalizeUnifiedId(item.selectionId)
			}))
		}));
	}, [isUnifiedFeedEnabled, persistedBets]);

	useEffect(() => {
		if (!enableWriting) {
			return;
		}
		setPersistedBets(bets.map(betToPersistedBet));
	}, [bets, setPersistedBets, enableWriting]);

	return unifiedPersistedBets;
};

export default usePersistedBets;
